import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useBlocker } from 'react-router-dom';
import { css } from '@emotion/react';
import * as Sentry from '@sentry/react';
import { Layout } from '~/components/SignIn';
import InnerLayout from '~/components/SignUp/Layout/InnerLayout';
import { Input } from '~/components/atomic';
import { InputTextarea } from '~/components/common';
import { Header } from '~/components/common/Header';
import { sendReport } from '~/api';
import { useModal } from '~/hooks';
import { ModalParams, SendReportRequest } from '~/types';
import useToast from '~/store/useToast';

export interface ReportProps {
  onNextStep: () => void;
}

export default function Report({ onNextStep }: ReportProps) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [subject, setSubject] = useState<string>('');
  const [explanation, setExplanation] = useState<string>('');
  const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
  const { openModal } = useModal();
  const hasUnsavedChanges: boolean = Boolean(subject) || Boolean(explanation);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      !isSubmitted &&
      hasUnsavedChanges &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const modalData: ModalParams = {
    title: 'Leave page',
    content:
      'If you leave, the current content will be deleted. Do you want to leave this page?',
    type: 'double_red',
    buttonName: 'Leave',
    click: () => {
      setSubject('');
      setExplanation('');
      navigate(-1);
    },
    cancelClick: () => {
      if (blocker.state === 'blocked') {
        blocker.reset();
      }
    },
  };

  useEffect(() => {
    if (isSubmitted) {
      onNextStep();
    }
  }, [isSubmitted, navigate]);

  useEffect(() => {
    if (blocker.state === 'blocked' && hasUnsavedChanges) {
      openModal(modalData);
    }
  }, [blocker.state, hasUnsavedChanges]);

  const handleOnClick = async () => {
    try {
      const requestData: SendReportRequest = {
        subject,
        explanation,
      };

      const { data } = await sendReport(requestData);
      if (data) setIsSubmitted(true);
    } catch (error) {
      console.error(error);
      showToast('error', 'Failed to send report. Please try again later.');
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        event.preventDefault();
        event.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [hasUnsavedChanges]);

  const handlePrevClick = useCallback(() => {
    if (hasUnsavedChanges) {
      openModal(modalData);
    } else {
      navigate(-1);
    }
  }, [hasUnsavedChanges]);

  const buttonProps = {
    title: 'Submit',
    disabled: subject.trim() === '' || explanation.trim() === '',
    onClick: handleOnClick,
  };

  return (
    <>
      <Header.Root>
        <Header.Section>
          <Header.PrevButton onPrevClick={handlePrevClick} />
          <Header.Title>Report</Header.Title>
        </Header.Section>
      </Header.Root>

      <Layout gap={24}>
        <InnerLayout buttonProps={buttonProps}>
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 16px;
            `}
          >
            <Input
              id='MypageReportSubject'
              label='Subject'
              placeholder='Report your problem here shortly'
              text={subject}
              setText={setSubject}
              textLengthOpt
            />
            <InputTextarea
              id='MypageReportExplanation'
              label='Explanation'
              placeholder='Please give us more details'
              text={explanation}
              setText={setExplanation}
              textLengthOpt
              maxText={1200}
              rows={3}
            />
          </div>
        </InnerLayout>
      </Layout>
    </>
  );
}
